import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";

const DocumentFormatSection = ({ config, handleInputChange, disabled }) => {
  const { t } = useTranslation();
  const isMarkdown = config.outputFormat === "markdown";

  return (
    <div className="param-group">
      <div className="param-group-header">
        <h3 className="param-group-title">
          <span>📄</span>
          {t("sections.format.title")}
        </h3>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="form-group md:col-span-2">
          <label className="form-label" htmlFor="outputFormat">
            {t("sections.format.outputFormat")}
          </label>
          <select
            id="outputFormat"
            className="form-input"
            value={config.outputFormat || "pdf"}
            onChange={(e) => handleInputChange("outputFormat", e.target.value)}
            disabled={disabled}
          >
            <option value="pdf">📄 {t("sections.format.outputFormat.pdf")}</option>
            <option value="markdown">
              📝 {t("sections.format.outputFormat.markdown")}
            </option>
          </select>
        </div>
        {!isMarkdown && (
          <>
        <div className="form-group">
          <label className="form-label" htmlFor="paperFormat">
            {t("sections.format.pageSize")}
          </label>
          <select
            id="paperFormat"
            className="form-input"
            value={config.paperFormat || "A4"}
            onChange={(e) => handleInputChange("paperFormat", e.target.value)}
            disabled={disabled}
          >
            <option value="A4">📄 {t("sections.format.pageSize.A4")}</option>
            <option value="A3">📄 {t("sections.format.pageSize.A3")}</option>
            <option value="US-Letter">
              📄 {t("sections.format.pageSize.US-Letter")}
            </option>
            <option value="US-Legal">
              📄 {t("sections.format.pageSize.US-Legal")}
            </option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="orientation">
            {t("sections.format.orientation")}
          </label>
          <select
            id="orientation"
            className="form-input"
            value={config.orientation || "portrait"}
            onChange={(e) => handleInputChange("orientation", e.target.value)}
            disabled={disabled}
          >
            <option value="portrait">
              📱 {t("sections.format.orientation.portrait")}
            </option>
            <option value="landscape">
              📺 {t("sections.format.orientation.landscape")}
            </option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="columnCount">
            {t("sections.format.columnCount")}
          </label>
          <select
            id="columnCount"
            className="form-input"
            value={config.columnCount || 1}
            onChange={(e) =>
              handleInputChange("columnCount", parseInt(e.target.value))
            }
            disabled={disabled}
          >
            <option value="1">📝 {t("sections.format.columnCount.1")}</option>
            <option value="2">📰 {t("sections.format.columnCount.2")}</option>
            <option value="3">📊 {t("sections.format.columnCount.3")}</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="textAlign">
            {t("sections.format.textAlign")}
          </label>
          <select
            id="textAlign"
            className="form-input"
            value={config.textAlign || "left"}
            onChange={(e) => handleInputChange("textAlign", e.target.value)}
            disabled={disabled}
          >
            <option value="left">
              ⬅️ {t("sections.format.textAlign.left")}
            </option>
            <option value="justify">
              📐 {t("sections.format.textAlign.justify")}
            </option>
            <option value="center">
              ⬜ {t("sections.format.textAlign.center")}
            </option>
            <option value="right">
              ➡️ {t("sections.format.textAlign.right")}
            </option>
          </select>
        </div>
          </>
        )}
      </div>

      {/* Option supplémentaire pour debug */}
      {!isMarkdown && (
      <>
      <div className="mt-4">
        <label className="flex gap-2 items-center">
          <input
            type="checkbox"
            checked={config.showBorders !== false}
            onChange={(e) => handleInputChange("showBorders", e.target.checked)}
            disabled={disabled}
            className="accent-primary"
          />
          <span className="text-sm" style={{ color: "var(--text-600)" }}>
            🔍 {t("sections.format.showBorders")}
          </span>
        </label>
      </div>
      <div
        className={clsx("mt-4 grid grid-cols-3 gap-4 ", {
          hidden: config.showBorders !== true,
        })}
      >
        <div className="form-group">
          <label className="form-label" htmlFor="borderWidth">
            {t("sections.format.borderWidth")}
          </label>
          <select
            id="borderWidth"
            className="form-input"
            value={config.borderWidth || 10}
            onChange={(e) =>
              handleInputChange("borderWidth", parseInt(e.target.value))
            }
            disabled={disabled}
          >
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
            <option value="6">6</option>
            <option value="7">7</option>
            <option value="8">8</option>
            <option value="10">10</option>
            <option value="12">12</option>
            <option value="14">14</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="borderOpacity">
            {t("sections.format.borderOpacity")}
          </label>
          <select
            id="borderOpacity"
            className="form-input"
            value={config.borderOpacity || 0.4}
            onChange={(e) =>
              handleInputChange("borderOpacity", parseFloat(e.target.value))
            }
            disabled={disabled}
          >
            <option value="0">0</option>
            <option value="0.05">0.05</option>
            <option value="0.1">0.1</option>
            <option value="0.2">0.2</option>
            <option value="0.3">0.3</option>
            <option value="0.4">0.4</option>
            <option value="0.5">0.5</option>
            <option value="0.6">0.6</option>
            <option value="0.7">0.7</option>
            <option value="0.8">0.8</option>
            <option value="0.9">0.9</option>
            <option value="0.95">0.95</option>
            <option value="1">1</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="borderInterval">
            {t("sections.format.borderInterval")}
          </label>
          <select
            id="borderInterval"
            className="form-input"
            value={config.borderInterval || 6}
            onChange={(e) =>
              handleInputChange("borderInterval", parseInt(e.target.value))
            }
            disabled={disabled}
          >
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="4">4</option>
            <option value="6">6</option>
            <option value="8">8</option>
            <option value="10">10</option>
            <option value="12">12</option>
          </select>
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default DocumentFormatSection;
